import { FiberManualRecord as DotIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useActivity } from '@/hooks/activity';
import { AuditLog, AuditAction, AuditResourceType } from '@/models/AuditLog';

// Check if activity is within last 24 hours
const isRecentActivity = (timestamp: number): boolean => {
  const now = Date.now();
  const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;
  return timestamp > twentyFourHoursAgo;
};

// Format action description from audit log
const formatActionDescription = (activity: AuditLog): string => {
  const actionVerbs: Record<AuditAction, string> = {
    [AuditAction.Viewed]: 'Viewed',
    [AuditAction.Edited]: 'Edited',
    [AuditAction.Created]: 'Created',
    [AuditAction.Deleted]: 'Deleted',
    [AuditAction.Invited]: 'Invited',
    [AuditAction.Completed]: 'Completed',
    [AuditAction.Uncompleted]: 'Uncompleted',
    [AuditAction.LoggedIn]: 'Logged in',
    [AuditAction.Uploaded]: 'Uploaded',
    [AuditAction.Messaged]: 'Sent a message',
  };

  const resourceLabels: Record<AuditResourceType, string> = {
    [AuditResourceType.Property]: 'property',
    [AuditResourceType.Document]: 'document',
    [AuditResourceType.Task]: 'task',
    [AuditResourceType.User]: 'user',
    [AuditResourceType.Score]: 'score',
    [AuditResourceType.Report]: 'report',
    [AuditResourceType.Message]: 'message',
    [AuditResourceType.RFQ]: 'RFQ',
  };

  const verb = actionVerbs[activity.action] || activity.action;
  const resourceLabel = resourceLabels[activity.resourceType] || activity.resourceType;

  // Special cases
  if (activity.action === AuditAction.Messaged) {
    return 'Sent a message';
  }

  if (activity.action === AuditAction.Invited && activity.resourceName) {
    return `${verb} ${activity.resourceName}`;
  }

  if (activity.action === AuditAction.LoggedIn) {
    return verb;
  }

  if (activity.resourceName) {
    return `${verb} ${activity.resourceName}`;
  }

  return `${verb} ${resourceLabel}`;
};

interface ActivityItemCardProps {
  activity: AuditLog;
  onOpenRFQ?: (rfqId: string) => void;
}

const ActivityItemCard = ({ activity, onOpenRFQ }: ActivityItemCardProps) => {
  const isNew = isRecentActivity(activity.timestamp);
  const actionDescription = formatActionDescription(activity);
  const relativeTime = formatDistanceToNow(activity.timestamp, { addSuffix: true });
  const isRFQ = activity.resourceType === AuditResourceType.RFQ;
  const canOpenRFQ = isRFQ && Boolean(activity.resourceId);

  if (isRFQ) {
    return (
      <Box
        sx={{
          width: '100%',
          py: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--tx-text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activity.userName}
            </Typography>
            {isNew && <DotIcon sx={{ fontSize: 10, color: '#C0FF02', flex: '0 0 auto' }} />}
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              color: 'rgb(115, 115, 115)',
              ml: 'auto',
              pl: 1,
              flex: '0 0 auto',
            }}
          >
            {relativeTime}
          </Typography>
        </Box>

        <Box
          component="button"
          type="button"
          disabled={!canOpenRFQ}
          onClick={() => {
            if (activity.resourceId) onOpenRFQ?.(activity.resourceId);
          }}
          sx={{
            mt: 0.75,
            p: 0,
            width: '100%',
            border: 'none',
            background: 'transparent',
            appearance: 'none',
            textAlign: 'left',
            cursor: canOpenRFQ ? 'pointer' : 'default',
            font: 'inherit',
            '&:disabled': {
              cursor: 'default',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', lineHeight: 1.2 }}>
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--tx-on-lime)',
                background: 'var(--tx-lime)',
                borderRadius: '4px',
                px: '6px',
                py: '1px',
                lineHeight: 1.2,
              }}
            >
              New
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--tx-muted)',
                letterSpacing: 0,
                lineHeight: 1.2,
              }}
            >
              Bid request
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--tx-muted)',
              mt: '10px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.35,
            }}
          >
            {activity.resourceName || 'Request for Quote'}
          </Typography>
          {activity.details && (
            <Typography
              sx={{
                fontSize: 11,
                color: 'rgb(115, 115, 115)',
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
              }}
            >
              {activity.details}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        py: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header row: User name, green dot, timestamp */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--tx-text)',
            }}
          >
            {activity.userName}
          </Typography>
          {isNew && <DotIcon sx={{ fontSize: 10, color: '#C0FF02' }} />}
        </Box>
        <Typography
          sx={{
            fontSize: 12,
            color: 'rgb(115, 115, 115)',
            ml: 'auto',
          }}
        >
          {relativeTime}
        </Typography>
      </Box>

      {/* Action description */}
      <Typography
        onClick={() => {
          if (canOpenRFQ && activity.resourceId) onOpenRFQ?.(activity.resourceId);
        }}
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: canOpenRFQ ? 'var(--tx-lime-ink)' : 'var(--tx-muted)',
          mt: 0.75,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: canOpenRFQ ? 'pointer' : 'default',
          textDecoration: canOpenRFQ ? 'underline' : 'none',
          textUnderlineOffset: '3px',
        }}
      >
        {actionDescription}
      </Typography>

      {/* Detail text */}
      {activity.details && (
        <Typography
          sx={{
            fontSize: 11,
            color: 'rgb(115, 115, 115)',
            mt: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {activity.details}
        </Typography>
      )}
    </Box>
  );
};

export const ActivityPanel = () => {
  const { selectedProperty } = usePropertiesContext();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    activities,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useActivity(selectedProperty?.property.id ?? '');

  const handleOpenRFQ = (rfqId: string) => {
    const canOpenOnCurrentRoute =
      location.pathname === '/dashboard/home-hub' ||
      location.pathname === '/dashboard/inspection-report';
    const next = new URLSearchParams(canOpenOnCurrentRoute ? location.search : undefined);
    next.set('rfqId', rfqId);

    navigate({
      pathname: canOpenOnCurrentRoute ? location.pathname : '/dashboard/inspection-report',
      search: `?${next.toString()}`,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!activities.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#666', fontSize: 13 }}>
          No activity for this property yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {activities.map((activity, index) => (
        <Box key={activity.id}>
          <ActivityItemCard activity={activity} onOpenRFQ={handleOpenRFQ} />
          {index < activities.length - 1 && (
            <Box
              sx={{
                width: '100%',
                height: '1px',
                backgroundColor: 'var(--tx-fill-2)',
              }}
            />
          )}
        </Box>
      ))}

      {/* Load More button */}
      {hasNextPage && (
        <Box sx={{ py: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            sx={{ textTransform: 'none' }}
          >
            {isFetchingNextPage ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : null}
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
